import { type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, CircleUser } from 'lucide-react';
import { apps, type PortalApp } from '@/apps/registry';
import { useSession } from '@/lib/session';

// The launcher dashboard: the post-login home. A hero header (greeting + date + portfolio
// stats) over a large colored app-icon grid of all apps (Microsoft 365 / Google Workspace
// app-suite style). Driven entirely by apps/registry, so a new app needs zero edits here.
export default function Launcher() {
  const { data: session } = useSession();
  const hour = new Date().getHours();
  const greeting =
    hour < 5 ? 'Good evening' : hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // Admin-only apps (e.g. Foundry Usage) are hidden from non-admins; their data API is gated too.
  const visibleApps = apps.filter((a) => !a.adminOnly || session?.isAdmin);
  const liveCount = visibleApps.filter((a) => a.status === 'live').length;
  const previewCount = visibleApps.length - liveCount;

  return (
    <div className="stagger">
      {/* ---- Hero header ---- */}
      <section className="launcher-hero mb-7 px-5 py-6 md:px-8 md:py-7">
        <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <span className="eyebrow">HMH Apps · Platform</span>
            <h1 className="mt-3 font-(family-name:--font-heading) text-3xl font-bold text-text md:text-4xl">
              {greeting}
            </h1>
            <p className="mt-2 text-muted">{today}</p>
            <div className="mt-4">
              <RoleChip isAdmin={!!session?.isAdmin} />
            </div>
          </div>

          {/* Portfolio stats */}
          <div className="flex items-stretch gap-6 sm:gap-8">
            <Stat value={visibleApps.length} label="Applications" />
            <Divider />
            <Stat value={liveCount} label="Live" accent />
            <Divider />
            <Stat value={previewCount} label="In preview" />
          </div>
        </div>
      </section>

      {/* ---- App icon grid (all apps) ---- */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
        {visibleApps.map((app) => (
          <LaunchItem key={app.id} app={app} />
        ))}
      </div>
    </div>
  );
}

/* ---------- pieces ---------- */

function Stat({ value, label, accent }: { value: number; label: string; accent?: boolean }) {
  return (
    <div className="flex flex-col justify-end">
      <span className={'hero-stat-value text-3xl font-bold md:text-4xl ' + (accent ? 'text-accent' : 'text-text')}>
        {value}
      </span>
      <span className="mt-1 font-(family-name:--font-ui) text-[0.7rem] uppercase tracking-[0.14em] text-muted">
        {label}
      </span>
    </div>
  );
}

function Divider() {
  return <span aria-hidden className="w-px self-stretch bg-border" />;
}

function RoleChip({ isAdmin }: { isAdmin: boolean }) {
  const Icon = isAdmin ? ShieldCheck : CircleUser;
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-xs text-muted shadow-[var(--shadow-sm)]">
      <Icon className="h-3.5 w-3.5 text-accent" aria-hidden />
      <span className="font-medium text-text">{isAdmin ? 'Administrator' : 'Member'}</span>
    </span>
  );
}

function LaunchItem({ app }: { app: PortalApp }) {
  const Icon = app.Icon;
  return (
    <Link to={app.to} className="launch-item group" title={app.blurb} aria-label={app.name}>
      <span className="app-icon-tile" style={{ '--tint': app.tint } as CSSProperties}>
        <Icon className="h-11 w-11" aria-hidden />
        {app.status === 'live' && <span className="live-dot launch-live" aria-hidden />}
      </span>
      <span className="launch-label">{app.name}</span>
    </Link>
  );
}
